/**
 * OrganizationClientPackagesTab Component
 *
 * Displays Healthie offerings (packages) assigned to an organization's Client Group.
 * Shows billing packages available for clients in this organization.
 */
import React, { useState, useEffect } from 'react';
import { useRecordContext } from 'react-admin';
import {
    Box, Typography, Alert, CircularProgress, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, TablePagination, Chip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const OrganizationClientPackagesTab = () => {
    const record = useRecordContext();
    
    // State for pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [packages, setPackages] = useState([]);
    const [totalPackages, setTotalPackages] = useState(0);
    const [loading, setLoading] = useState(true);
    
    const healthieData = record?.healthie_data;
    const hasClientGroupId = record?.client_group_id;
    
    useEffect(() => {
        if (healthieData?.offerings) {
            loadPackages();
        } else if (healthieData) {
            // If healthieData exists but no offerings, it means no packages found
            setPackages([]);
            setTotalPackages(0);
            setLoading(false);
        }
    }, [healthieData?.offerings, page, rowsPerPage]);
    
    const loadPackages = () => {
        setLoading(true);
        try {
            const allPackages = healthieData?.offerings || [];
            const startIndex = page * rowsPerPage;
            const endIndex = startIndex + rowsPerPage;
            
            setPackages(allPackages.slice(startIndex, endIndex));
            setTotalPackages(allPackages.length);
        } catch (err) {
            console.error('Error loading Healthie offerings:', err);
        } finally {
            setLoading(false);
        }
    };
    
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    
    const formatPrice = (price) => {
        if (!price || price === '0.0' || price === '0') return 'Free';
        return `$${parseFloat(price).toFixed(2)}`;
    };
    
    const formatBillingFrequency = (frequency, repeatTimes) => {
        if (!frequency) return 'One-time';
        if (frequency === 'once') return 'One-time';
        
        let label = frequency.charAt(0).toUpperCase() + frequency.slice(1);
        if (repeatTimes && repeatTimes !== '0') {
            label += ` (${repeatTimes}x)`;
        }
        return label;
    };
    
    if (!hasClientGroupId) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info" icon={<InfoOutlinedIcon />}>
                    <Typography variant="body2">
                        This organization is not linked to a Healthie Client Group.
                    </Typography>
                </Alert>
            </Box>
        );
    }
    
    if (!healthieData) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="warning" icon={<InfoOutlinedIcon />}>
                    <Typography variant="body2">
                        <strong>Unable to load Healthie data</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        The EHR system might be temporarily unavailable, the request may have timed out, or you don't have permission to view this data. 
                        Please refresh the page to try again.
                    </Typography>
                </Alert>
            </Box>
        );
    }
    
    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">
                    Client Packages ({totalPackages})
                </Typography>
                <Chip 
                    label="Healthie Billing" 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    icon={<CheckCircleOutlineIcon />}
                />
            </Box>
            
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={1}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Package Name</strong></TableCell>
                                <TableCell><strong>Price</strong></TableCell>
                                <TableCell><strong>Billing</strong></TableCell>
                                <TableCell><strong>Purchases</strong></TableCell>
                                <TableCell><strong>Revenue</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {packages.map((pkg) => (
                                <TableRow key={pkg.id}>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2" fontWeight={500}>
                                                {pkg.name || 'N/A'}
                                            </Typography>
                                            {pkg.description && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {pkg.description.length > 80 
                                                        ? `${pkg.description.substring(0, 80)}...` 
                                                        : pkg.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{formatPrice(pkg.price)}</TableCell>
                                    <TableCell>
                                        {formatBillingFrequency(pkg.billing_frequency, pkg.repeat_times)}
                                    </TableCell>
                                    <TableCell>{pkg.user_package_selections_count || 0}</TableCell>
                                    <TableCell>
                                        {pkg.total_revenue 
                                            ? `$${parseFloat(pkg.total_revenue).toFixed(2)}` 
                                            : '$0.00'}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {pkg.archived && (
                                                <Chip label="Archived" size="small" color="default" />
                                            )}
                                            {pkg.show_offering && !pkg.archived && (
                                                <Chip label="Visible" size="small" color="success" />
                                            )}
                                            {!pkg.show_offering && !pkg.archived && (
                                                <Chip label="Hidden" size="small" color="warning" />
                                            )}
                                            {pkg.offering_includes_count > 0 && (
                                                <Chip 
                                                    label={`${pkg.offering_includes_count} appt types`} 
                                                    size="small" 
                                                    color="info"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {packages.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            No packages found for this organization in Healthie.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalPackages}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </TableContainer>
            )}
        </Box>
    );
};

export default OrganizationClientPackagesTab;

